import { z } from "zod";

/**
 * A bot key's allowlist entry: either an exact origin
 * (`https://shop.example.com`) or a wildcard over its subdomains
 * (`https://*.example.com`). The wildcard is only ever the whole leftmost
 * label, matches subdomains at any depth, and deliberately does not match
 * the bare domain itself — `https://example.com` is listed on its own, the
 * same way a CSP source list reads.
 */
const WILDCARD_PREFIX = "*.";

interface ParsedPattern {
  protocol: string;
  port: string;
  hostname: string;
  wildcard: boolean;
}

function parsePattern(value: string): ParsedPattern | undefined {
  const match = /^([a-z][a-z0-9+.-]*:\/\/)(\*\.)?(.*)$/i.exec(value.trim());
  if (!match) return undefined;
  const [, scheme, star, rest] = match;
  if (rest!.includes("*")) return undefined;

  let url: URL;
  try {
    url = new URL(`${scheme}${rest}`);
  } catch {
    return undefined;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
  if (url.username || url.password || url.search || url.hash || url.pathname !== "/") return undefined;
  // A wildcard over a single label (`https://*.com`) would be every site on a TLD.
  if (star && !url.hostname.includes(".")) return undefined;

  return { protocol: url.protocol, port: url.port, hostname: url.hostname, wildcard: Boolean(star) };
}

function formatPattern({ protocol, port, hostname, wildcard }: ParsedPattern): string {
  return `${protocol}//${wildcard ? WILDCARD_PREFIX : ""}${hostname}${port ? `:${port}` : ""}`;
}

/**
 * Validates an allowlist entry and normalizes it to the form a browser's
 * `Origin` header takes (lowercased host, no trailing slash, default port
 * dropped), so an entry typed as `https://Shop.example.com/` still matches.
 */
export const allowedOriginSchema = z.string().transform((value, ctx) => {
  const parsed = parsePattern(value);
  if (!parsed) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `"${value}" is not an origin like https://example.com or https://*.example.com` });
    return z.NEVER;
  }
  return formatPattern(parsed);
});

/** Whether one `Origin` header value is covered by one allowlist entry. */
export function originMatches(origin: string, pattern: string): boolean {
  const allowed = parsePattern(pattern);
  if (!allowed) return false;

  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (url.protocol !== allowed.protocol || url.port !== allowed.port) return false;
  if (!allowed.wildcard) return url.hostname === allowed.hostname;
  return url.hostname.endsWith(`.${allowed.hostname}`);
}

/** Whether a request's `Origin` header is covered by any entry of a key's allowlist. */
export function isOriginAllowed(origin: string | undefined, allowedOrigins: readonly string[]): boolean {
  if (!origin) return false;
  return allowedOrigins.some((pattern) => originMatches(origin, pattern));
}
